import React, { useState } from "react";
import useFetchTableData from "../../hooks/useFetchTableData";
import adminApi from "../../api/admin";
import { Check, Eye, X, Search, Filter, RefreshCw, Plus } from "lucide-react";
import { formatDateForDisplay, getFullName } from "../../utils/helpers";
import { GridActionsCellItem } from "@mui/x-data-grid";
import DataTable from "../../components/common/DataTable";
import { Link } from "react-router-dom";
import { Card, CardContent, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import Button from "../../components/common/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import UserManagementModal from "../../components/admin/UserManagementModal";

const UserManagementPage = ({ onNavigate }) => {
  const [filters, setFilters] = useState({
    search: "",
    role: "all",
    status: "all",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [errors, setErrors] = useState({});
    const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isError,
    error,
    paginationModel,
    setPaginationModel,
  } = useFetchTableData({
    queryKey: "users",
    fetchFunc: adminApi.getUsers,
    params: filters,
  });

  // Mutation for creating a new user
  const createUserMutation = useMutation({
    mutationFn: (userData) => adminApi.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsModalOpen(false);
      toast.success('User created successfully!');
    },
    onError: (error) => {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.message || 'Failed to create user');
      setErrors(error.response?.data)
    }
  });

  // Mutation for updating an existing user
  const updateUserMutation = useMutation({
    mutationFn: ({ id, userData }) => adminApi.updateUser(id, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-details', id] });
      setIsModalOpen(false);
      toast.success('User updated successfully!');
      refetch();
    },
    onError: (error) => {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
      setErrors(error.response?.data)
    }
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    // Reset to first page when filters change
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      role: "all",
      status: "all",
    });
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleCreateNew = () => {
    setInitialData(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setInitialData(null);
  };

  const handleEdit = (user) => {
    console.log('Edit user:', user);
    setInitialData(user);
    setIsModalOpen(true);
  };

  const handleSubmitUser = (userData) => {
    if (initialData) {
      // Update existing user
      updateUserMutation.mutate({ id: initialData.id, userData });
    } else {
      // Create new user
      createUserMutation.mutate(userData);
    }
  };

  const columns = [
    {
      field: "email",
      headerName: "Email",
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Link
          to={`${params.row.id}`}
          className="text-blue-600 hover:underline"
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: "full_name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || getFullName(params.row.first_name, params.row.last_name) || "N/A",
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "is_email_verified",
      headerName: "Email Verified",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <div className="flex justify-center items-center w-full h-full">
          {params.value ? (
            <Check color="green" className="w-4 h-4" />
          ) : (
            <X color="red" className="w-4 h-4" />
          )}
        </div>
      ),
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => {
        const status = params.value;
        const statusConfig = {
          true: { color: "bg-green-100 text-green-700", label: "Active" },
          false: { color: "bg-red-100 text-red-700", label: "Inactive" },
        };

        const config = statusConfig[status] || {
          color: "bg-gray-100 text-gray-700",
          label: status,
        };

        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
          >
            {config.label}
          </span>
        );
      },
    },
    {
      field: "last_login",
      headerName: "Last Login",
      flex: 1,
      minWidth: 120,
      type: "date",
      valueGetter: (params) => (params ? new Date(params) : null),
      renderCell: (params) => {
        return params.row.last_login
          ? formatDateForDisplay(params.row.last_login)
          : "";
      },
    },
  ];

  const handleView = (job) => {
    console.log("View job:", job);
    onNavigate(`/admin/jobs/${job.id}`);
  };

  if (isError) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading users: {error?.message}</p>
        </div>
      </div>
    );
  }

  const hasActiveFilters = filters.search || filters.role !== "all" || filters.status !== "all";

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">
            User Management
          </h1>
          <p className="text-secondary-600 mt-1">
            Total: {data?.count || 0} users
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
                  <Button 
                    onClick={clearFilters}
                    variant="outline"
                    disabled={!hasActiveFilters}
                    icon={<RefreshCw className="w-4 h-4 mr-2" />}
                  >
                    Clear Filters
                  </Button>
                  <Button 
                              onClick={handleCreateNew}
                              icon={<Plus className="w-4 h-4 mr-2" />}
                            >
                              Create New User
                            </Button>
                </div>
      </div>

      <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <Filter className="w-5 h-5 text-secondary-500" />
                    <Typography variant="subtitle1" className="text-secondary-900">
                      Filters
                    </Typography>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                    <TextField
                      size="small"
                      placeholder="Search users..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange("search", e.target.value)}
                      slotProps={{input:{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search className="w-4 h-4 text-gray-400" />
                          </InputAdornment>
                        ),
                      }}}
                      className="w-full sm:w-64"
                    />
                    <FormControl size="small" className="min-w-[150px]">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filters.status}
                        label="Status"
                        onChange={(e) => handleFilterChange("status", e.target.value)}
                      >
                        <MenuItem value="all">All Statuses</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" className="min-w-[150px]">
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={filters.role}
                        label="Status"
                        onChange={(e) => handleFilterChange("role", e.target.value)}
                      >
                        <MenuItem value="all">All Roles</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="recruiter">Recruiter</MenuItem>
                        <MenuItem value="professional">Professional</MenuItem>
                        
                      </Select>
                    </FormControl>
                  </div>
                </div>
              </CardContent>
            </Card>

      {/* Filters Section */}
      {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear all filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                id="search"
                placeholder="Search by email or name..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

         
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              id="role"
              value={filters.role}
              onChange={(e) => handleFilterChange("role", e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
              <option value="employer">Employer</option>
              <option value="candidate">Candidate</option>
            </select>
          </div>

         
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        
        {hasActiveFilters && (
          <div className="mt-4 flex flex-wrap gap-2">
            {filters.search && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Search: "{filters.search}"
                <button
                  onClick={() => handleFilterChange("search", "")}
                  className="ml-1.5 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.role !== "all" && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Role: {filters.role}
                <button
                  onClick={() => handleFilterChange("role", "all")}
                  className="ml-1.5 hover:bg-green-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.status !== "all" && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Status: {filters.status}
                <button
                  onClick={() => handleFilterChange("status", "all")}
                  className="ml-1.5 hover:bg-purple-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div> */}

      <DataTable
        rows={data?.results}
        columns={columns}
        loading={isLoading}
        rowCount={data?.count}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
      />

      <UserManagementModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitUser}
        initialData={initialData}
        isLoading={createUserMutation.isPending || updateUserMutation.isPending}
        title={initialData ? "Edit User" : "Create User"}
        submitButtonText={initialData ? "Update User" : "Create User"}
        errors={errors}
        setErrors={setErrors}
      />
    </div>
  );
};

export default UserManagementPage;